import {
  Children,
  createContext,
  Fragment,
  PureComponent,
  ReactNode,
} from 'react';

import { token } from '@atlaskit/tokens';

import { stopPropagation } from '../utils/events';

import SidebarCollapsed from './sidebar-collapsed';
import SidebarExpanded from './sidebar-expanded';
import { SplitBar } from './split-bar';
import * as styles from './styles';
import {
  Cards,
  CollapsedContentProps,
  ResizeProps,
  SidebarContent,
} from './types';

export type SidebarProps = {
  ariaLabel?: string;
  backgroundColor?: string;
  children?: ReactNode | ReactNode[];
  contentOnly?: boolean;
  collapsedContent: SidebarContent[];
  collapsedWidth: number;
  expandedContent: SidebarContent[];
  expandedWidth: number;
  icon?: JSX.Element;
  isCollapsed: boolean;
  isCollapsible?: boolean;
  maxExpandedWidth: number;
  minExpandedWidth: number;
  onResize: (props: ResizeProps) => void;
  resizable?: boolean;
  toggleButtonCollapseLabel: string;
  toggleButtonExpandLabel: string;
  tabs?: ReactNode;
  onCollapsedContentClick: (props: CollapsedContentProps) => void;
};

export type SidebarState = {
  isCollapsed: boolean;
  shouldAnimate: boolean;
  width: number;
};

export const SidebarChildContext = createContext<number>(0);

export function createChildWrapper(
  keyPrefix: 'collapsed' | 'expanded',
  onToggle?: (anchorId: string) => void
) {
  return (
    sidebarCard: SidebarContent,
    sidebarIndex: number,
    allSidebarCards: SidebarContent[]
  ) => {
    // reverse the "natural" z-index stacking order to allow InlineMessage components
    // in card titles to overlap cards below them
    const zIndex = allSidebarCards.length - sidebarIndex;

    return (
      <Fragment key={`${keyPrefix}-${sidebarIndex}`}>
        <styles.ScrollToAnchor id={`${keyPrefix}-${sidebarCard.type}`} />
        <styles.SidebarChild
          data-testid={`${keyPrefix}-${sidebarCard.type}`}
          key={`${keyPrefix}-${sidebarIndex}`}
          onClick={() =>
            keyPrefix === 'collapsed' && onToggle
              ? onToggle(sidebarCard.type)
              : stopPropagation
          }
          style={{ zIndex }}
        >
          <SidebarChildContext.Provider value={sidebarIndex}>
            {sidebarCard.content}
          </SidebarChildContext.Provider>
        </styles.SidebarChild>
      </Fragment>
    );
  };
}

// TODO: Replace usage of unsafe lifecycle methods
// eslint-disable-next-line react/no-unsafe, import/no-default-export
export default class Sidebar extends PureComponent<SidebarProps, SidebarState> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    backgroundColor: token('elevation.surface.sunken'),
    collapsedContent: [],
    collapsedWidth: 64,
    expandedContent: [],
    expandedWidth: 264,
    isCollapsed: false,
    maxExpandedWidth: 800,
    minExpandedWidth: 200,
    onResize: () => {},
    resizable: true,
    onCollapsedContentClick: () => {},
    isCollapsible: true,
    isSingleElement: false,
  };

  state = {
    isCollapsed: this.props.isCollapsed,
    shouldAnimate: true,
    width: this.props.isCollapsed
      ? this.props.collapsedWidth
      : this.props.expandedWidth,
  };

  /* eslint react/no-unsafe: "warn" */
  UNSAFE_componentWillReceiveProps(nextProps: SidebarProps) {
    if (this.state.isCollapsed !== nextProps.isCollapsed) {
      this.toggleSidebar(nextProps.isCollapsed);
    }
  }

  fireOnResize() {
    const { isCollapsed, width } = this.state;
    this.props.onResize({ isCollapsed, width });
  }

  handleToggleSidebar = () => {
    this.toggleSidebar();
  };

  handleToggleCollapsedSidebar = (cardType: string) => {
    let index = 0;
    if (cardType === Cards.FileTree) {
      index = 1;
    } else if (cardType === Cards.Activities) {
      index = 2;
    }
    const anchorId = `expanded-${cardType}`;
    this.toggleSidebar();
    this.props.onCollapsedContentClick({ id: anchorId, tabIndex: index });
  };

  handleResize = (offset: number) => {
    this.setState(oldState => {
      const {
        collapsedWidth,
        maxExpandedWidth,
        minExpandedWidth,
        isCollapsible,
      } = this.props;
      let newWidth = oldState.width + offset;

      const isCollapsed = !!(newWidth < minExpandedWidth && isCollapsible);

      if (newWidth > maxExpandedWidth) {
        newWidth = maxExpandedWidth;
      }

      if (isCollapsible) {
        if (newWidth < collapsedWidth) {
          newWidth = collapsedWidth;
        }
      } else {
        if (newWidth < minExpandedWidth) {
          newWidth = minExpandedWidth;
        }
      }

      return { isCollapsed, shouldAnimate: false, width: newWidth };
    });
  };

  handleResizeComplete = () => {
    this.setState(oldState => {
      const { width } = oldState;
      const { collapsedWidth, minExpandedWidth } = this.props;
      const newWidth = width < minExpandedWidth ? collapsedWidth : width;
      return { shouldAnimate: true, width: newWidth };
    }, this.fireOnResize);
  };

  toggleSidebar(isCollapsed?: boolean) {
    this.setState(oldState => {
      const { collapsedWidth, expandedWidth, isCollapsible } = this.props;
      const newIsCollapsed =
        isCollapsed !== undefined ? isCollapsed : !oldState.isCollapsed;

      if (newIsCollapsed && !isCollapsible) {
        return {
          isCollapsed: false,
          width: oldState.width,
        };
      }

      const width = newIsCollapsed ? collapsedWidth : expandedWidth;
      return { isCollapsed: newIsCollapsed, width };
    }, this.fireOnResize);
  }

  render() {
    const { isCollapsed } = this.state;
    const {
      backgroundColor,
      toggleButtonCollapseLabel,
      toggleButtonExpandLabel,
      ariaLabel,
      tabs,
      collapsedContent,
      expandedContent,
      children,
      contentOnly,
    } = this.props;

    const hasOnlyOneChildElement = Children.count(children) !== 0;

    return (
      <styles.SidebarContainer aria-label={ariaLabel}>
        <styles.SidebarControls>
          {this.props.isCollapsible && (
            <styles.Arrow
              aria-label={
                isCollapsed
                  ? toggleButtonExpandLabel
                  : toggleButtonCollapseLabel
              }
              aria-expanded={!isCollapsed}
              isCollapsed={isCollapsed}
              onClick={this.handleToggleSidebar}
              data-testid={
                isCollapsed
                  ? 'expand-sidebar-button'
                  : 'collapse-sidebar-button'
              }
            />
          )}
          {this.props.resizable ? (
            <SplitBar
              onClick={this.handleToggleSidebar}
              onResize={this.handleResize}
              onResizeComplete={this.handleResizeComplete}
            />
          ) : null}
        </styles.SidebarControls>
        <styles.Sidebar
          shouldAnimate={this.state.shouldAnimate}
          style={{ width: this.state.width }}
          data-testid="sidebar"
          backgroundColor={backgroundColor}
        >
          <styles.SidebarContent
            contentOnly={contentOnly}
            hasOneChildElement={hasOnlyOneChildElement}
          >
            <SidebarCollapsed
              isVisible={isCollapsed}
              icon={this.props.icon}
              onToggle={this.handleToggleSidebar}
              width={this.state.width}
              contentOnly={contentOnly}
            >
              {collapsedContent.map(
                createChildWrapper(
                  'collapsed',
                  this.handleToggleCollapsedSidebar
                )
              )}
            </SidebarCollapsed>
            <SidebarExpanded
              isVisible={!isCollapsed}
              width={this.state.width}
              contentOnly={contentOnly}
            >
              {children ||
                tabs ||
                expandedContent.map(createChildWrapper('expanded'))}
            </SidebarExpanded>
          </styles.SidebarContent>
        </styles.Sidebar>
      </styles.SidebarContainer>
    );
  }
}
